import { useState } from 'react';
import { trackEvent } from '@/lib/analytics';
import { useLocation } from 'react-router-dom';
import LocalizedLink from './LocalizedLink';
import { Menu, X, User, LogOut, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import BookingLink from '@/components/BookingLink';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { user, signOut } = useAuth();

  const serviceLinks = [
    { name: 'AI Websites', href: '/services/ai-websites' },
    { name: 'AI Chatbots & Lead Capture', href: '/services/ai-chatbots' },
    { name: 'CRM Automation', href: '/services/crm-automation' },
    { name: 'Workflow Automation', href: '/services/workflow-automation' },
    { name: 'AI Voice Agents', href: '/services/ai-voice-agents' },
    { name: 'Custom AI Agents', href: '/services/ai-agents' },
    { name: 'AI Audit & Strategy', href: '/services/ai-audit' },
    { name: 'View All Services', href: '/services' },
  ];

  // Header is intentionally lean: only conversion-critical surfaces plus
  // About live here. Blog, Case Studies, Careers, and FAQ are one scroll
  // away in the footer — see src/components/Footer.tsx.
  const primaryLinks = [
    { name: t('nav.products'), href: '/products' },
    { name: 'Work', href: '/work' },
    { name: 'Marketing', href: '/marketing-packages' },
    { name: t('nav.pricing'), href: '/pricing' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith('/services');

  const handleSignOut = async () => {
    await signOut();
  };

  // Close the mobile menu and collapse the Services submenu together.
  const closeMenu = () => {
    setIsOpen(false);
    setServicesOpen(false);
  };

  return (
    <nav id="main-navigation" className="bg-background/80 backdrop-blur-xl sticky top-0 z-50 border-b border-border" role="navigation" aria-label="Main navigation">
      <div className="container-width">
        <div className="relative flex justify-between items-center h-16 gap-4">
          {/* Logo — anchored to the left on every breakpoint; the hamburger
              sits on the right via justify-between. */}
          <LocalizedLink
            to="/"
            aria-label={t('nav.brainfyHome')}
            className="flex items-center gap-2 group transition-transform hover:scale-105 shrink-0"
          >
            {/* Full brand lockup (2026 rebrand) — clean-extracted transparent
                cutout of the Brainfy logo, exported at 2x for retina. */}
            <img
              src="/brainfy-lockup.png"
              alt="Brainfy"
              width={108}
              height={40}
              className="h-10 w-auto"
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  isServicesActive() ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('nav.services')} <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background border-border z-50">
                {serviceLinks.map((service) => (
                  <DropdownMenuItem key={service.href} asChild>
                    <LocalizedLink
                      to={service.href}
                      className={`flex items-center gap-2 ${
                        isActive(service.href) ? 'text-primary' : 'text-foreground hover:text-foreground'
                      }`}
                    >
                      {service.name}
                    </LocalizedLink>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {primaryLinks.map((item) => (
              <LocalizedLink
                key={item.href}
                to={item.href}
                className={`nav-underline text-sm font-medium transition-colors ${
                  isActive(item.href) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.name}
              </LocalizedLink>
            ))}

            <Button
                asChild
                size="sm"
                className="btn-primary"
                onClick={() =>
                  trackEvent('cta_click', {
                    cta_label: 'Book a Free Consultation',
                    cta_location: 'navigation-desktop',
                  })
                }
              >
                <BookingLink location="navigation-desktop" />
              </Button>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2 border-border bg-muted hover:bg-muted text-foreground">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background border-border z-50">
                  <DropdownMenuItem asChild>
                    <LocalizedLink to="/dashboard" className="flex items-center gap-2 text-foreground hover:text-foreground">
                      <User className="h-4 w-4" />
                      Dashboard
                    </LocalizedLink>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted" />
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 text-destructive">
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile menu button — ml-auto pushes right since the logo is
              absolute-positioned on mobile and no longer a flex sibling. */}
          <div className="lg:hidden ml-auto">
            <button
              onClick={() => { setIsOpen((v) => !v); setServicesOpen(false); }}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              aria-label={t('nav.toggleMenu')}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted text-foreground hover:bg-muted"
            >
              {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              <span className="sr-only">{t('nav.toggleMenu')}</span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div id="mobile-menu" className="lg:hidden py-4 border-t border-border">
            <div className="flex flex-col space-y-4">
              <LocalizedLink
                to="/"
                onClick={closeMenu}
                className={`text-sm font-medium transition-colors ${
                  isActive('/') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('nav.home')}
              </LocalizedLink>

              {/* Mobile Services Section — collapsed by default, expands on tap */}
              <div>
                <button
                  type="button"
                  onClick={() => setServicesOpen((v) => !v)}
                  aria-expanded={servicesOpen}
                  aria-controls="mobile-services-submenu"
                  className={`flex w-full items-center justify-between text-sm font-medium transition-colors ${
                    isServicesActive() ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t('nav.services')}
                  <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div id="mobile-services-submenu" className="mt-3 pl-4 space-y-3">
                    {serviceLinks.map((service) => (
                      <LocalizedLink
                        key={service.href}
                        to={service.href}
                        onClick={closeMenu}
                        className={`block text-sm transition-colors ${
                          isActive(service.href) ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {service.name}
                      </LocalizedLink>
                    ))}
                  </div>
                )}
              </div>

              {primaryLinks.map((item) => (
                <LocalizedLink
                  key={item.href}
                  to={item.href}
                  onClick={closeMenu}
                  className={`text-sm font-medium transition-colors ${
                    isActive(item.href) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item.name}
                </LocalizedLink>
              ))}

              <Button
                  asChild
                  className="btn-primary w-full"
                  onClick={() =>
                    trackEvent('cta_click', {
                      cta_label: 'Book a Free Consultation',
                      cta_location: 'navigation-mobile',
                    })
                  }
                >
                  <BookingLink location="navigation-mobile" onClick={closeMenu} />
                </Button>

              {user && (
                <div className="flex flex-col gap-2 pt-2 border-t border-border">
                  <Button asChild className="w-full btn-secondary">
                    <LocalizedLink to="/dashboard" onClick={closeMenu}>
                      Dashboard
                    </LocalizedLink>
                  </Button>
                  <Button onClick={() => { handleSignOut(); closeMenu(); }} variant="destructive" className="w-full">
                    Sign Out
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
